// Recovering a troubleshooting send whose outcome the iPad doesn't know.
//
// Every Start and every answer carries a client id (D-REV13). The worker
// treats a resend with the same id as "give me what you stored": the turns
// and reply if they landed, 202 { pending } while the AI is still answering,
// or a fresh run if nothing was stored. So a lost response is recovered by
// resending with the SAME id, and never makes a duplicate turn.

/** A new id for one Start or one answer: [A-Za-z0-9-], ≤ 64 chars. */
export function newClientId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * What a failed send means for the next attempt:
 *   'resend' — the outcome is unknown (network drop, timeout, Cancel, 5xx):
 *              the worker may have stored it, so retry with the same id.
 *   'reload' — 409: another send took this turn; re-read the session.
 *   'final'  — the worker refused it (4xx, or the 503 daily budget), so
 *              nothing was stored and the next send gets a new id.
 */
export function sendOutcome(err) {
  const status = err && err.status;
  if (status === 409) return 'reload';
  if (typeof status !== 'number' || (status >= 500 && status !== 503)) return 'resend';
  return 'final';
}

/** True when the conversation ends on the tech's turn: a reply is still owed. */
export function awaitingReply(turns) {
  return Array.isArray(turns) && turns.length > 0 && turns[turns.length - 1].role === 'tech';
}

/**
 * Run `send` (a Start or an answer with a fixed client id) until the worker
 * stops answering 202 { pending }, resending every `intervalMs` — a replay
 * is cheap, it never calls the AI. Past `deadlineMs` it throws an error with
 * no status, which sendOutcome() reads as "resend later". An aborted
 * `signal` (the tech's Cancel, or End session) stops the wait with an
 * AbortError.
 */
export async function untilAnswered(send, {
  intervalMs = 4000,
  deadlineMs = 180_000,
  signal = null,
  now = () => Date.now(),
  sleep = abortableSleep,
} = {}) {
  const started = now();
  for (;;) {
    const data = await send();
    if (!data || !data.pending) return data;
    if (signal && signal.aborted) throw abortError();
    if (now() - started + intervalMs > deadlineMs) {
      throw new Error('The reply is taking unusually long. Tap Try again in a minute — your answer won’t be sent twice.');
    }
    await sleep(intervalMs, signal);
    if (signal && signal.aborted) throw abortError();
  }
}

// Wakes early when `signal` aborts, so Cancel answers at once rather than
// after the rest of the wait.
function abortableSleep(ms, signal) {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
  });
}

function abortError() {
  const err = new Error('Stopped waiting');
  err.name = 'AbortError';
  return err;
}
